// Ruta: apps/dashboard/src/router.tsx
import React, { lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ProtectedRoute } from '@heyshaker/auth'
import { ROLES } from '@heyshaker/shared'

// Eager-loaded pages
import { LoginPage } from './pages/auth/LoginPage.js'
import { UnauthorizedPage } from './pages/auth/UnauthorizedPage.js'

// Lazy-loaded pages (code-split per route)
const DashboardLayout = lazy(() =>
  import('./layouts/DashboardLayout.js').then((m) => ({ default: m.DashboardLayout }))
)

// Arrendatarios (admin user management)
const ArrendatariosPage = lazy(() =>
  import('./pages/users/ArrendatariosPage.js').then((m) => ({ default: m.ArrendatariosPage }))
)
const UserDetailPage = lazy(() =>
  import('./pages/users/UserDetailPage.js').then((m) => ({ default: m.UserDetailPage }))
)

// Ads
const AdsPage = lazy(() =>
  import('./pages/ads/AdsPage.js').then((m) => ({ default: m.AdsPage }))
)

// Help
const HelpPage = lazy(() =>
  import('./pages/help/HelpPage.js').then((m) => ({ default: m.HelpPage }))
)

// Overview + stores
const OverviewPage = lazy(() =>
  import('./pages/overview/OverviewPage.js').then((m) => ({ default: m.OverviewPage }))
)
const StoreDashboardPage = lazy(() =>
  import('./pages/stores/StoreDashboardPage.js').then((m) => ({ default: m.StoreDashboardPage }))
)

// Devices
const DevicesPage = lazy(() =>
  import('./pages/devices/DevicesPage.js').then((m) => ({ default: m.DevicesPage }))
)
const DeviceDetailPage = lazy(() =>
  import('./pages/devices/DeviceDetailPage.js').then((m) => ({ default: m.DeviceDetailPage }))
)

// Inventory
const InventoryPage = lazy(() =>
  import('./pages/inventory/InventoryPage.js').then((m) => ({ default: m.InventoryPage }))
)
const InventoryTrendPage = lazy(() =>
  import('./pages/inventory/InventoryTrendPage.js').then((m) => ({
    default: m.InventoryTrendPage,
  }))
)

// Reports
const ProfitPage = lazy(() =>
  import('./pages/reports/ProfitPage.js').then((m) => ({ default: m.ProfitPage }))
)
const WeeklySalesPage = lazy(() =>
  import('./pages/reports/WeeklySalesPage.js').then((m) => ({ default: m.WeeklySalesPage }))
)
const SalesReportPage = lazy(() =>
  import('./pages/reports/SalesReportPage.js').then((m) => ({ default: m.SalesReportPage }))
)

// Products — Drinks (read-only legacy pages)
const LegacyDrinksPage = lazy(() =>
  import('./pages/products/DrinksPage.js').then((m) => ({ default: m.DrinksPage }))
)
const DrinkDetailPage = lazy(() =>
  import('./pages/products/DrinkDetailPage.js').then((m) => ({ default: m.DrinkDetailPage }))
)

// Drinks — full CRUD (migrated from CMS)
const DrinksListPage = lazy(() =>
  import('./pages/drinks/DrinksListPage.js').then((m) => ({ default: m.DrinksListPage }))
)
const CreateDrinkPage = lazy(() =>
  import('./pages/drinks/CreateDrinkPage.js').then((m) => ({ default: m.CreateDrinkPage }))
)

// Products — Snacks
const SnacksPage = lazy(() =>
  import('./pages/products/SnacksPage.js').then((m) => ({ default: m.SnacksPage }))
)
const SnackDetailPage = lazy(() =>
  import('./pages/products/SnackDetailPage.js').then((m) => ({ default: m.SnackDetailPage }))
)

// Orders
const OrdersPage = lazy(() =>
  import('./pages/orders/OrdersPage.js').then((m) => ({ default: m.OrdersPage }))
)
const OrderDetailPage = lazy(() =>
  import('./pages/orders/OrderDetailPage.js').then((m) => ({ default: m.OrderDetailPage }))
)

// Transactions / Movimientos (Revenue Details) — módulo chino agregado 2026-09
const TransactionsPage = lazy(() =>
  import('./pages/transactions/TransactionsPage.js').then((m) => ({ default: m.TransactionsPage }))
)

// Alerts
const AlertsPage = lazy(() =>
  import('./pages/alerts/AlertsPage.js').then((m) => ({ default: m.AlertsPage }))
)

// Payment methods
const PaymentMethodsPage = lazy(() =>
  import('./pages/payment-methods/PaymentMethodsPage.js').then((m) => ({
    default: m.PaymentMethodsPage,
  }))
)

// Monitoring
const MonitoringPage = lazy(() =>
  import('./pages/monitoring/MonitoringPage.js').then((m) => ({
    default: m.MonitoringPage,
  }))
)

// Feedback / Encuestas
const FeedbackPage = lazy(() =>
  import('./pages/feedback/FeedbackPage.js').then((m) => ({ default: m.FeedbackPage }))
)

// Settings
const NotificationSettingsPage = lazy(() =>
  import('./pages/settings/NotificationSettingsPage.js').then((m) => ({ default: m.NotificationSettingsPage }))
)

export function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected: all authenticated users */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/overview" replace />} />

        {/* Overview — all roles */}
        <Route
          path="overview"
          element={
            <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
              <OverviewPage />
            </ProtectedRoute>
          }
        />

        {/* Store-scoped routes */}
        <Route path="stores/:storeId">
          {/* Store dashboard */}
          <Route index element={<StoreDashboardPage />} />

          {/* Reports */}
          <Route path="profit" element={<ProfitPage />} />
          <Route path="weekly" element={<WeeklySalesPage />} />
          <Route path="reports" element={<SalesReportPage />} />

          {/* Devices */}
          <Route path="devices" element={<DevicesPage />} />
          <Route path="devices/:deviceId" element={<DeviceDetailPage />} />

          {/* Inventory (scoped to a device) */}
          <Route path="devices/:deviceId/inventory" element={<InventoryPage />} />
          <Route path="devices/:deviceId/inventory/trend" element={<InventoryTrendPage />} />

          {/* Drinks — full CRUD (list + create + legacy catalog detail) */}
          <Route
            path="drinks"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <DrinksListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="drinks/new"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ARRENDATARIO]}>
                <CreateDrinkPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="drinks/catalog"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <LegacyDrinksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="drinks/:drinkId"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <DrinkDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Snacks catalog */}
          <Route
            path="snacks"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <SnacksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="snacks/:snackId"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <SnackDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Orders */}
          <Route
            path="orders"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="orders/:orderId"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Transactions / Movimientos (Revenue Details) */}
          <Route
            path="transactions"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <TransactionsPage />
              </ProtectedRoute>
            }
          />

          {/* Alerts */}
          <Route path="alerts" element={<AlertsPage />} />

          {/* Ads/Publicidad */}
          <Route path="ads" element={<AdsPage />} />

          {/* Feedback/Encuestas */}
          <Route path="feedback" element={<FeedbackPage />} />

          {/* Help */}
          <Route path="help" element={<HelpPage />} />

          {/* Payment methods */}
          <Route
            path="payment-methods"
            element={
              <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.ARRENDATARIO]}>
                <PaymentMethodsPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Arrendatarios — admin + superadmin */}
        <Route
          path="arrendatarios"
          element={
            <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN]}>
              <ArrendatariosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="arrendatarios/:userId"
          element={
            <ProtectedRoute roles={[ROLES.SUPERADMIN, ROLES.ADMIN]}>
              <UserDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Monitoring — superadmin only */}
        <Route
          path="monitoring"
          element={
            <ProtectedRoute roles={[ROLES.SUPERADMIN]}>
              <MonitoringPage />
            </ProtectedRoute>
          }
        />
        {/* Settings */}
        <Route path="settings/notifications" element={<NotificationSettingsPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
